const VISUAL_VIEWPORT_WIDTH_VAR = "--visual-viewport-width";
const VISUAL_VIEWPORT_LEFT_VAR = "--visual-viewport-left";
const VISUAL_VIEWPORT_TOP_VAR = "--visual-viewport-top";
const IOS_PLATFORM_PATTERN = /iPhone|iPad|iPod/i;
const MAXIMUM_SCALE_PATTERN = /(?:^|,)\s*maximum-scale\s*=/i;

export interface IOSInputZoomRuntime {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
}

function formatViewportPixels(value: number): string {
  if (!Number.isFinite(value)) {
    return "0px";
  }
  return `${Math.max(0, Math.round(value * 100) / 100)}px`;
}

function normalizeViewportPart(value: string): string {
  return value.trim();
}

export function shouldApplyIOSInputZoomViewportGuard(input: IOSInputZoomRuntime): boolean {
  const platform = input.platform.trim();
  const userAgent = input.userAgent.trim();
  if (IOS_PLATFORM_PATTERN.test(platform) || IOS_PLATFORM_PATTERN.test(userAgent)) {
    return true;
  }
  return platform === "MacIntel" && Number.isFinite(input.maxTouchPoints) && input.maxTouchPoints > 1;
}

export function appendMaximumScaleViewportGuard(content: string): string {
  if (MAXIMUM_SCALE_PATTERN.test(content)) {
    return content;
  }
  const parts = content.split(",").map(normalizeViewportPart).filter((part) => part !== "");
  return [...parts, "maximum-scale=1.0"].join(", ");
}

function applyIOSInputZoomViewportGuard(): void {
  if (
    !shouldApplyIOSInputZoomViewportGuard({
      userAgent: window.navigator.userAgent,
      platform: window.navigator.platform,
      maxTouchPoints: window.navigator.maxTouchPoints,
    })
  ) {
    return;
  }
  const viewportMeta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (viewportMeta === null) {
    return;
  }
  viewportMeta.setAttribute("content", appendMaximumScaleViewportGuard(viewportMeta.getAttribute("content") ?? ""));
}

export function setupVisualViewportLayoutBridge(): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  applyIOSInputZoomViewportGuard();

  const root = document.documentElement;
  const visualViewport = window.visualViewport;

  if (visualViewport === undefined || visualViewport === null) {
    root.style.setProperty(VISUAL_VIEWPORT_WIDTH_VAR, formatViewportPixels(window.innerWidth));
    root.style.setProperty(VISUAL_VIEWPORT_LEFT_VAR, "0px");
    root.style.setProperty(VISUAL_VIEWPORT_TOP_VAR, "0px");
    return;
  }

  let frameID: number | null = null;

  const applyViewportMetrics = () => {
    frameID = null;
    root.style.setProperty(VISUAL_VIEWPORT_WIDTH_VAR, formatViewportPixels(visualViewport.width));
    root.style.setProperty(VISUAL_VIEWPORT_LEFT_VAR, formatViewportPixels(visualViewport.offsetLeft));
    root.style.setProperty(VISUAL_VIEWPORT_TOP_VAR, formatViewportPixels(visualViewport.offsetTop));
  };

  const scheduleViewportMetricsUpdate = () => {
    if (frameID !== null) {
      return;
    }
    frameID = window.requestAnimationFrame(applyViewportMetrics);
  };

  applyViewportMetrics();

  visualViewport.addEventListener("resize", scheduleViewportMetricsUpdate);
  visualViewport.addEventListener("scroll", scheduleViewportMetricsUpdate);
  window.addEventListener("resize", scheduleViewportMetricsUpdate);
  window.addEventListener("orientationchange", scheduleViewportMetricsUpdate);
  document.addEventListener("visibilitychange", scheduleViewportMetricsUpdate);
}
